/**
 * AddTankModal — Phase 24 / Task 24.3 (DEC-094).
 *
 * Add OR edit a tank on a battery. Optionally pick from the standard tank
 * catalog to auto-fill capacity + bbl/inch + type (the hauler can still override
 * any field). On edit the standard picker is hidden (edit patches fields directly).
 */
import * as React from 'react';
import { standardTankLabel } from '@lodex-pro/shared/tank-strapping';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { fieldStyle, labelStyle, FormRow, ProducerIdHint } from './form-bits';
import {
  TankThresholdFields,
  thresholdPatch,
  thresholdValuesFrom,
  type TankThresholdValues,
} from './TankThresholdFields';
import { useAddTank, useUpdateTank, useStandardTanks, type CatalogTank } from './hooks';

export interface AddTankModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly batteryId: string;
  readonly editing?: CatalogTank | null;
}

function toNum(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function AddTankModal({
  open,
  onOpenChange,
  batteryId,
  editing,
}: AddTankModalProps): React.JSX.Element {
  const isEdit = editing != null;
  const { data: stdData } = useStandardTanks();
  // /v1/platform/tank-catalog returns all rows incl. inactive — the picker only
  // offers active ones (Task 24.5).
  const standardTanks = (stdData?.standardTanks ?? []).filter((s) => s.isActive);

  const [standardId, setStandardId] = React.useState('');
  const [name, setName] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [bblPerInch, setBblPerInch] = React.useState('');
  const [tankType, setTankType] = React.useState('');
  const [producerId, setProducerId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  // Phase 13.4a (0236) — the haul threshold, edited with the tank.
  const [threshold, setThreshold] = React.useState<TankThresholdValues>(
    thresholdValuesFrom(editing ?? null),
  );

  React.useEffect(() => {
    if (!open) return;
    setStandardId('');
    setName(editing?.name ?? '');
    setCapacity(editing?.capacityBbl ?? '');
    setBblPerInch(editing?.bblPerInch ?? '');
    setTankType(editing?.tankType ?? '');
    setProducerId(editing?.producerIdentifier ?? '');
    setThreshold(thresholdValuesFrom(editing ?? null));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const addMutation = useAddTank(batteryId, () => {
    toast.success('Tank added');
    onOpenChange(false);
  });
  const updateMutation = useUpdateTank(editing?.id ?? '', batteryId);
  const pending = addMutation.isPending || updateMutation.isPending;

  const onPickStandard = (id: string): void => {
    setStandardId(id);
    const std = standardTanks.find((s) => s.id === id);
    if (std) {
      setCapacity(std.capacityBbl);
      setBblPerInch(std.bblPerInch);
      if (name.trim() === '') setName(std.name);
    }
  };

  const handleSave = (): void => {
    if (name.trim() === '') {
      setError('Tank name is required.');
      return;
    }
    setError(null);
    const onError = (): void => setError('Could not save the tank. Try again.');
    if (isEdit) {
      const th = thresholdPatch(threshold);
      if (!th.ok) {
        setError(th.error);
        return;
      }
      updateMutation.mutate(
        {
          ...th.body,
          name: name.trim(),
          capacityBbl: toNum(capacity) ?? null,
          bblPerInch: toNum(bblPerInch) ?? null,
          tankType: tankType.trim() === '' ? null : tankType.trim(),
          producerIdentifier: producerId.trim() === '' ? null : producerId.trim(),
        },
        {
          onSuccess: () => {
            toast.success('Tank updated');
            onOpenChange(false);
          },
          onError,
        },
      );
      return;
    }
    const cap = toNum(capacity);
    const bpi = toNum(bblPerInch);
    addMutation.mutate(
      {
        name: name.trim(),
        // Conditional spread — exactOptionalPropertyTypes forbids explicit undefined.
        ...(cap !== undefined && { capacityBbl: cap }),
        ...(bpi !== undefined && { bblPerInch: bpi }),
        ...(tankType.trim() !== '' && { tankType: tankType.trim() }),
        ...(producerId.trim() !== '' && { producerIdentifier: producerId.trim() }),
        ...(standardId !== '' && { standardTankCatalogId: standardId }),
      },
      { onError },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit tank' : 'Add tank'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!isEdit && (
            <FormRow label="Standard tank (optional — auto-fills)" htmlFor="tank-std">
              <Combobox
                id="tank-std"
                options={standardTanks.map((s) => ({ value: s.id, label: standardTankLabel(s) }))}
                value={standardId}
                onChange={onPickStandard}
                placeholder="Pick from catalog…"
                searchPlaceholder="Search standard tanks…"
                emptyText="No standard tanks."
              />
            </FormRow>
          )}
          <FormRow label="Name" htmlFor="tank-name" required>
            <input
              id="tank-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. Tank 1"
              style={fieldStyle}
            />
          </FormRow>
          <div style={{ display: 'flex', gap: '12px' }}>
            <FormRow label="Capacity (bbl)" htmlFor="tank-cap">
              <input
                id="tank-cap"
                inputMode="decimal"
                value={capacity}
                onChange={(e): void => setCapacity(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="BBL per inch" htmlFor="tank-bpi">
              <input
                id="tank-bpi"
                inputMode="decimal"
                value={bblPerInch}
                onChange={(e): void => setBblPerInch(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <FormRow label="Tank type" htmlFor="tank-type">
            <input
              id="tank-type"
              value={tankType}
              onChange={(e): void => setTankType(e.target.value)}
              placeholder="e.g. GUN BARREL or WATER"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Producer identifier" htmlFor="tank-pid">
            <input
              id="tank-pid"
              value={producerId}
              onChange={(e): void => setProducerId(e.target.value)}
              placeholder="What the producer calls this tank"
              aria-describedby={producerId.trim() === '' ? 'tank-pid-hint' : undefined}
              style={fieldStyle}
            />
            <ProducerIdHint id="tank-pid-hint" value={producerId} />
          </FormRow>
          {/* Task 24.5 — provenance hint when a catalog entry seeded the fields. */}
          {standardId !== '' &&
            (() => {
              const std = standardTanks.find((s) => s.id === standardId);
              return std ? (
                <p
                  style={{
                    ...labelStyle,
                    textTransform: 'none',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Based on: {std.name}
                </p>
              ) : null;
            })()}
          {isEdit && (
            <TankThresholdFields value={threshold} onChange={setThreshold} idPrefix="tank-th" />
          )}
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
